import Button from "../../student/components/generic/button";
import { CopyToClipboardButton } from "../../manager/components/Experiment/CopyToClipboardButton";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { deltamathAPI } from "../../manager/utils";
import { IntegrationInfoType } from "./IntegrationLanding";
import NonSuperAdminCredentials from "./NonSuperAdminCredentials";
import { useDeltaToastContext } from "../../shared/contexts/ToasterContext";
import EditIntegrationForm from "./EditIntegrationForm";
import ViewIntegrationData from "./ViewIntegrationData";

export interface LtiConfigurationPayload {
  issuer: string;
  clientId: string;
  deploymentId: string;
}

const BLANK_DATA: LtiConfigurationPayload = {
  issuer: "",
  clientId: "",
  deploymentId: "",
};

type Props = {
  integration: IntegrationInfoType;
  lti_1_3_credentials?: {
    issuer: string;
    client_id: string;
    deployment_id: string;
  };
};

const Integration13 = (props: Props) => {
  const [pageState, setPageState] = useState<"edit" | "view">(
    props.lti_1_3_credentials && !props.lti_1_3_credentials.client_id
      ? "edit"
      : "view"
  );
  const [integrationError, setIntegrationError] = useState<boolean>(false);

  const toastContext = useDeltaToastContext();
  const resourceUrls =
    props.integration.type === "canvas"
      ? [
          {
            label: "Copy JSON URL",
            url: "https://files.deltamath.com/canvas-configuration-1-3.json",
          },
          {
            label: "Copy Redirect URLs",
            url: "https://www.deltamath.com/lti/1_3/main",
          },
        ]
      : [
          {
            label: "Copy JWKS URL",
            url: "https://www.deltamath.com/lti/1_3/keys",
          },
          {
            label: "Copy OIDC Login Init Url",
            url: "https://www.deltamath.com/lti/1_3/login",
          },
          {
            label: "Copy Domain / URL",
            url: "https://www.deltamath.com/lti/1_3/main",
          },
          {
            label: "Copy Redirect URLs",
            url: "https://www.deltamath.com/lti/1_3/main",
          },
        ];

  const mutation = useMutation({
    mutationFn: (body: {
      issuer: string;
      clientId: string;
      deploymentId: string;
      integrationType: string;
    }) => {
      return axios.post<{ success: boolean }>(
        deltamathAPI() + "/admin_new/data/testLtiCredentials",
        body,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    onSuccess: (data: { data: { success: boolean } }) => {
      if (data.data.success) {
        toastContext.addToast({
          title: "Success",
          message: "LTI 1.3 credentials saved",
          status: "Success",
        });
        setPageState("view");
        setIntegrationError(false);
      } else {
        setIntegrationError(true);
        toastContext.addToast({
          title: "Error",
          message:
            "Unable to validate credentials, please check your credentials and try again.",
          status: "Error",
        });
      }
    },
    onError: () => {
      setIntegrationError(true);
      toastContext.addToast({
        title: "Error",
        message:
          "Unable to validate credentials, please check your credentials and try again.",
        status: "Error",
      });
    },
  });

  const [data, setData] = useState<LtiConfigurationPayload>(BLANK_DATA);

  useEffect(() => {
    if (props.lti_1_3_credentials) {
      setData({
        issuer: props.lti_1_3_credentials.issuer,
        clientId: props.lti_1_3_credentials.client_id,
        deploymentId: props.lti_1_3_credentials.deployment_id,
      });
    }
  }, [props.lti_1_3_credentials, setData]);

  const testIntegration = () => {
    mutation.mutate({
      issuer: data.issuer,
      clientId: data.clientId,
      deploymentId: data.deploymentId,
      integrationType: props.integration.type,
    });
  };

  return (
    <div className="bg-dm-background-blue-100 p-6">
      <div className="rounded-lg border-b border-dm-charcoal-100 bg-white p-6">
        <div className="border-b border-dm-charcoal-100 pb-4">
          <h3 className="font-medium leading-6 text-dm-gray-800">
            Integration Guide
          </h3>
          <div className="my-4">
            <Button
              type="outline"
              className="mb-8 mt-4"
              href={
                props.integration.type === "canvas"
                  ? `https://files.deltamath.com/Canvas-Site-Guide-1-3.pdf`
                  : ``
              }
              target="_blank"
            >
              {props.integration.title} LTI 1.3 Guide{" "}
              <i className="far fa-external-link-alt ml-2"></i>
            </Button>
          </div>
          <h3 className="mb-2 font-medium leading-6 text-dm-gray-800">
            Resource URLs
          </h3>
          <div className="grid max-w-xl grid-cols-2 gap-4">
            {resourceUrls.map((url) => (
              <div key={url.label} className="flex items-center gap-2">
                <p className="text-sm text-dm-brand-blue-500">{url.label}</p>
                <CopyToClipboardButton value={url.url} className="ml-2" />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6">
          <h3 className="mb-4 font-medium leading-6 text-dm-gray-800">
            Configure LTI
          </h3>
          <div>
            {!props.lti_1_3_credentials && (
              <div className="mb-6 flex w-fit gap-1 bg-dm-charcoal-100 p-2">
                <i className="far fa-exclamation-triangle pt-1" />
                <NonSuperAdminCredentials />
              </div>
            )}
            {pageState === "edit" ? (
              <div>
                {integrationError && (
                  <div className="mb-4 flex max-w-md flex-col gap-2">
                    <p className="text-dm-error-500">
                      There was an error with your Issuer and/or Client ID.
                      Please verify the information and test again.
                    </p>
                    <p className="text-dm-error-500">
                      If you continue to have issues please reach out to{" "}
                      <a
                        href="mailto:support@deltamath.com"
                        className="text-dm-brand-blue-500 underline"
                      >
                        support@deltamath.com
                      </a>
                      .
                    </p>
                  </div>
                )}

                <EditIntegrationForm
                  data={data}
                  setData={setData}
                  integrationLoaded={!!props.lti_1_3_credentials}
                  integration={props.integration}
                  testIntegration={testIntegration}
                />
              </div>
            ) : (
              <ViewIntegrationData
                data={data}
                changePageState={() => setPageState("edit")}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integration13;
